import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../../../lib/api";

const CLS_STYLE = { HOT: "bg-red-500/15 text-red-400", WARM: "bg-amber-500/15 text-amber-400", INFORMATION: "bg-slate-500/15 text-slate-400" };
const STATUSES = ["new", "contacted", "converted", "closed"];

export default function AiLeads() {
  const [leads, setLeads] = useState([]);
  const [cls, setCls] = useState("");

  const load = useCallback(() => {
    api.get(`/admin/ai/leads${cls ? `?classification=${cls}` : ""}`)
      .then((r) => setLeads(r.data)).catch(() => toast.error("Failed to load leads"));
  }, [cls]);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try {
      await api.post(`/admin/ai/leads/${id}/status`, { status });
      toast.success("Lead updated");
      load();
    } catch { toast.error("Update failed"); }
  };

  return (
    <div className="p-4 sm:p-6" data-testid="ai-leads-page">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-xl font-bold text-white">AI Leads</h1>
          <p className="text-sm text-slate-400">Captured automatically by the assistant. Classifications are internal only.</p>
        </div>
        <div className="flex gap-1.5" data-testid="ai-leads-filters">
          {["", "HOT", "WARM", "INFORMATION"].map((c) => (
            <button key={c} onClick={() => setCls(c)} data-testid={`ai-leads-filter-${c || "all"}`}
                    className={`rounded-full px-3 py-1.5 text-xs font-bold transition-colors ${cls === c ? "bg-saffron text-white" : "border border-adminborder text-slate-300 hover:bg-white/5"}`}>
              {c || "All"}
            </button>
          ))}
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border border-adminborder" data-testid="ai-leads-table">
        <table className="w-full min-w-[760px] text-left text-sm">
          <thead className="bg-admincard text-xs uppercase text-slate-500">
            <tr>
              <th className="px-3 py-2.5">Customer</th><th className="px-3 py-2.5">Enquiry</th>
              <th className="px-3 py-2.5">Destination</th><th className="px-3 py-2.5">Weight</th>
              <th className="px-3 py-2.5">Class</th><th className="px-3 py-2.5">Assigned</th>
              <th className="px-3 py-2.5">Created</th><th className="px-3 py-2.5">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-adminborder bg-admincard/50">
            {leads.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-8 text-center text-slate-500">No leads yet.</td></tr>
            )}
            {leads.map((l) => (
              <tr key={l.id} data-testid={`ai-lead-row-${l.id}`}>
                <td className="px-3 py-2.5">
                  <p className="font-semibold text-white">{l.name || "—"}</p>
                  <p className="font-mono text-xs text-slate-400">{l.phone || "—"}</p>
                </td>
                <td className="px-3 py-2.5 text-xs text-slate-300">{(l.enquiry_type || "").replace(/_/g, " ")}
                  {l.contents ? <p className="text-[11px] text-slate-500">{l.contents}</p> : null}</td>
                <td className="px-3 py-2.5 text-slate-300">{l.destination || "—"}</td>
                <td className="px-3 py-2.5 text-slate-300">{l.weight ? `${l.weight} kg` : "—"}</td>
                <td className="px-3 py-2.5">
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${CLS_STYLE[l.classification] || CLS_STYLE.INFORMATION}`}
                        data-testid={`ai-lead-cls-${l.id}`}>{l.classification}</span>
                </td>
                <td className="px-3 py-2.5 text-xs text-slate-400">{l.assigned_to || "—"}</td>
                <td className="px-3 py-2.5 text-xs text-slate-500">{String(l.created_at || "").slice(0, 10)}</td>
                <td className="px-3 py-2.5">
                  <select value={l.status} onChange={(e) => setStatus(l.id, e.target.value)}
                          data-testid={`ai-lead-status-${l.id}`}
                          className="rounded-lg border border-adminborder bg-adminbg px-2 py-1 text-xs text-slate-200">
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
